import { Event, EventBatch, ApiResponse } from './types';
import { generateId, debugLog } from './utils';

export class EventManager {
  private eventQueue: Event[] = [];
  private readonly batchSize: number;
  private readonly flushInterval: number;
  private flushTimer: NodeJS.Timeout | null = null;
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private debug: boolean | undefined;
  private readonly maxRetries: number = 3;
  private currentExternalPlayerId: string | null = null;

  constructor(
    apiKey: string,
    baseUrl = 'https://api.tula.dev',
    batchSize = 10,
    flushInterval = 5000,
    debug?: boolean
  ) {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl;
    this.batchSize = batchSize;
    this.flushInterval = flushInterval;
    this.debug = debug;
    this.startFlushTimer();
  }

  addEvent(event: Omit<Event, 'id' | 'timestamp'>): void {
    const fullEvent: Event = {
      ...event,
      id: generateId(),
      timestamp: new Date(),
      retryCount: 0
    };

    this.eventQueue.push(fullEvent);
    debugLog(this.debug, 'Added event to queue:', fullEvent);

    if (this.eventQueue.length >= this.batchSize) {
      this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.eventQueue.length === 0) {
      debugLog(this.debug, 'No events to flush');
      return;
    }

    const eventsToSend = this.eventQueue.splice(0, this.batchSize);
    const batch: EventBatch = {
      events: eventsToSend,
      timestamp: new Date()
    };

    debugLog(this.debug, `Flushing ${eventsToSend.length} events`);

    try {
      await this.sendBatch(batch);
      debugLog(this.debug, 'Events sent successfully');
    } catch (error) {
      debugLog(this.debug, 'Failed to send events:', error);
      
      // Only retry events that haven't exceeded max retries
      const retryableEvents = eventsToSend.filter(event => {
        const currentRetryCount = (event.retryCount || 0) + 1;
        if (currentRetryCount <= this.maxRetries) {
          event.retryCount = currentRetryCount;
          return true;
        } else {
          debugLog(this.debug, `Dropping event ${event.id} after ${this.maxRetries} failed attempts`);
          return false;
        }
      });
      
      if (retryableEvents.length > 0) {
        this.eventQueue.unshift(...retryableEvents);
        debugLog(this.debug, `Retrying ${retryableEvents.length} events (${eventsToSend.length - retryableEvents.length} dropped)`);
      }
    }
  }

  setCurrentPlayer(externalPlayerId: string): void {
    this.currentExternalPlayerId = externalPlayerId;
  }

  private async sendBatch(batch: EventBatch): Promise<ApiResponse> {
    // Convert EventManager's batch format to SDK log-event format
    const eventsForSDK = batch.events.map(event => ({
      playerId: event.playerId,
      userId: this.currentExternalPlayerId || event.playerId,
      sessionId: event.sessionId,
      eventType: event.eventType,
      itemId: event.itemId,
      skuVariantId: event.skuVariantId,
      experimentId: event.experimentId,
      experimentArmId: event.experimentArmId,
      properties: event.properties || {}
    }));

    const response = await fetch(`${this.baseUrl}/api/sdk/log-event`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
        'X-SDK-Version': '0.1.1'
      },
      body: JSON.stringify({
        events: eventsForSDK,
        playerId: batch.events[0]?.playerId,
        userId: this.currentExternalPlayerId || batch.events[0]?.playerId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  private startFlushTimer(): void {
    if (typeof setInterval !== 'undefined') {
      this.flushTimer = setInterval(() => {
        this.flush();
      }, this.flushInterval);
    }
  }

  async destroy(): Promise<void> {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    await this.flush();
    debugLog(this.debug, 'EventManager destroyed');
  }
}